import React, { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ChannelPicker } from '../../components/ChannelPicker'
import { useChannels } from '../../hooks/useChannels'
import { useChannelCategories } from '../../hooks/useChannelCategories'
import {
  ChannelFilters,
  ChannelSortBy,
  ChannelSortOrder,
  ChannelType,
  DEFAULT_CHANNEL_FILTERS,
} from '../../../domain'

const filtersFromSearchParams = (params: URLSearchParams): ChannelFilters => {
  const categories = params.get('categories')
  const types = params.get('types')

  return {
    search: params.get('search') ?? '',
    categories: categories ? categories.split(',').filter(Boolean) : [],
    types: types ? (types.split(',').filter(Boolean) as ChannelType[]) : [],
    sortBy: (params.get('sortBy') as ChannelSortBy) ?? DEFAULT_CHANNEL_FILTERS.sortBy,
    sortOrder: (params.get('sortOrder') as ChannelSortOrder) ?? DEFAULT_CHANNEL_FILTERS.sortOrder,
  }
}

const filtersToSearchParams = (filters: ChannelFilters): URLSearchParams => {
  const params = new URLSearchParams()
  if (filters.search) params.set('search', filters.search)
  if (filters.categories?.length) params.set('categories', filters.categories.join(','))
  if (filters.types?.length) params.set('types', filters.types.join(','))
  if (filters.sortBy) params.set('sortBy', filters.sortBy)
  if (filters.sortOrder) params.set('sortOrder', filters.sortOrder)

  return params
}

const Channels: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const filters = useMemo(() => filtersFromSearchParams(searchParams), [searchParams])

  const { channels, pagination, isLoading, refresh, countChannels } = useChannels({ loadOnMount: true, filters })
  const { categories } = useChannelCategories()

  const perPage = pagination?.perPage || 10

  const handleFiltersChange = useCallback((next: ChannelFilters) => {
    setSearchParams(filtersToSearchParams(next))
    refresh(1, perPage, next)
  }, [perPage, refresh, setSearchParams])

  const handlePageChange = useCallback((page: number, pageSize: number) => {
    refresh(page, pageSize, filters)
  }, [filters, refresh])

  const handleLoadMore = useCallback(() => {
    refresh((pagination?.page || 1) + 1, perPage, filters, true)
  }, [filters, pagination?.page, perPage, refresh])

  return (
    <ChannelPicker
      channels={channels}
      categories={categories}
      isLoading={isLoading}
      pagination={{ page: pagination?.page || 1, perPage, total: pagination?.total || 0 }}
      filters={filters}
      selectedChannels={{}}
      showSelection={false}
      onFiltersChange={handleFiltersChange}
      onSelectionChange={() => undefined}
      onPageChange={handlePageChange}
      onLoadMore={handleLoadMore}
      onPreviewCount={countChannels}
    />
  )
}

export default Channels
